import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, ContentTile } from '@as/cotopaxi';
import { SlideType, } from '@as-react/common-types';
import { useAemConfig } from '@as-react/common-components';
import { isNullOrUndefined } from '@as-react/common-utils';
import { getBoxPropsFromAEMPaddings } from '../util/AEMUtil';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import CategoryTileContainer from './CategoryTileContainer';
import SingleProductTileContainer from './SingleProductTileContainer';
import TeaserContainer from './TeaserContainer';
import { getPictureSrcset } from '../util/ImageUtil';
import { singleProductTileActions } from '../redux/single-product-tile/actions/SingleProductTileActions.creators';
import { Slider } from '../components/slider/Slider';
import VideoPlayer from '../components/video-player/VideoPlayer';
const MINIMUM_AMOUNT_OF_SLIDES_TO_REMOVE_LARGE_RENDITIONS_FOR = 2;
const LARGE_RENDITION_THRESHOLD = 600;
const SliderContainer = ({ paddings, cdnSettings, height, properties, slides, sliderId, }) => {
    const { t } = useTranslation([I18N_NAMESPACES.general]);
    const { imageCdnPrefix } = useAemConfig();
    const contentTileTranslations = {
        readMore: t(`${I18N_NAMESPACES.general}:readMore`),
    };
    const dispatch = useDispatch();
    useEffect(() => {
        if (slides.some(slide => slide.type === SlideType.SINGLE_PRODUCT_TILE)) {
            const products = [];
            slides.forEach(slide => {
                if ('initializationProduct' in slide) {
                    products.push(slide.initializationProduct);
                }
            });
            dispatch(singleProductTileActions.addBulkProductsToList(products));
        }
    }, []);
    function getContentTileSrcSet(image, imageCdnPrefix) {
        let srcSet = getPictureSrcset(image, imageCdnPrefix);
        if (properties.slidesToShowDesktop > MINIMUM_AMOUNT_OF_SLIDES_TO_REMOVE_LARGE_RENDITIONS_FOR) {
            srcSet = Object.fromEntries(Object.entries(srcSet).filter(([key]) => {
                const parsedKey = parseInt(key);
                return isNaN(parsedKey) || parsedKey < LARGE_RENDITION_THRESHOLD;
            }));
        }
        return srcSet;
    }
    const renderSlide = useCallback((_a) => {
        var { type } = _a, slideProps = __rest(_a, ["type"]);
        switch (type) {
            case SlideType.CATEGORY_TILE:
                return _jsx(CategoryTileContainer, Object.assign({}, slideProps));
            case SlideType.TEASER:
                return _jsx(TeaserContainer, Object.assign({}, slideProps, { mainCdnSettings: cdnSettings }));
            case SlideType.SINGLE_PRODUCT_TILE:
                return (_jsx(SingleProductTileContainer, Object.assign({}, slideProps, { withoutSpacing: true, shouldInitProduct: false, adjustTileDescriptionHeight: slideProps.tileFormat === 'simple' })));
            case SlideType.CONTENT_TILE: {
                const _b = slideProps, { image } = _b, contentTileProps = __rest(_b, ["image"]);
                return (_jsx(ContentTile, Object.assign({}, contentTileProps, { translations: contentTileTranslations, image: isNullOrUndefined(image)
                        ? { src: '' }
                        : {
                            src: image.imagePath,
                            srcset: getContentTileSrcSet(image, imageCdnPrefix),
                            isPicture: true,
                            lazy: true,
                        }, withoutSpacing: true })));
            }
            case SlideType.VIDEO:
                return _jsx(VideoPlayer, Object.assign({}, slideProps));
            default:
                return null;
        }
    }, [slides]);
    return (_jsx(Box, Object.assign({}, getBoxPropsFromAEMPaddings(paddings), { children: _jsx(Slider, { properties: properties, id: sliderId, height: height, children: slides.map((slide, index) => (_jsx("div", { style: slide.type === SlideType.SINGLE_PRODUCT_TILE ? { height: 'auto' } : undefined, children: renderSlide(slide) }, `slide_component_${index}`))) }) })));
};
export default SliderContainer;
